/**
 * The subjects and domains of the report card.
 *
 * The report does not use the twelve entries of `SUBJECTS` directly. It uses
 * the blocks of the bulletin the teacher kept in a spreadsheet before this app
 * existed: one block per line of the report, some of them merging two subjects
 * of the timetable, each scored out of 100 for a period.
 *
 * A block either carries named domains — français and mathématiques do, and
 * each domain is then marked out of 25 — or none at all, in which case its
 * evaluations sit in a single pool marked straight out of 100. That is exactly
 * what the spreadsheet does: its `lire` sheet totals 25 per period where its
 * `Sciences` sheet totals 100.
 *
 * Every name in `SUBJECTS` belongs to exactly one block, which is what stops an
 * evaluation from becoming unreachable in the class grid.
 */
import { CURRICULUM_SUBJECT_NAMES } from './subjects.ts';

export interface BulletinSubject {
  /** Stable key, used as a `<select>` value. Never persisted. */
  key: string;
  label: string;
  /** The `SUBJECTS` names whose evaluations fall into this block. */
  subjects: readonly string[];
  /** Empty means one implicit domain, and an average out of 100. */
  domains: readonly string[];
}

/** A domain is marked out of this; a whole block out of 100. */
export const DOMAIN_SCALE = 25;
export const BULLETIN_SCALE = 100;

export const BULLETIN_SUBJECTS: readonly BulletinSubject[] = [
  {
    key: 'francais',
    label: 'Français',
    subjects: ['Français'],
    domains: ['Lire', 'Écrire', 'Parler', 'Écouter'],
  },
  {
    key: 'maths',
    label: 'Mathématiques',
    subjects: ['Mathématiques'],
    domains: ['Nombres', 'Solides et figures', 'Grandeurs', 'Traitement de données'],
  },
  {
    key: 'sciences',
    label: 'Éveil scientifique',
    subjects: ['Éveil scientifique'],
    domains: [],
  },
  {
    // Merged on the report, so merged here: the two are one line of the
    // bulletin and one table in the class grid.
    key: 'hist-geo',
    label: 'Éveil historique et géographique',
    subjects: ['Éveil historique', 'Éveil géographique'],
    domains: [],
  },
  {
    key: 'fes',
    label: 'Formation économique et sociale',
    subjects: ['Formation économique et sociale'],
    domains: [],
  },
  {
    key: 'fmttn',
    label: 'Formation manuelle, technique, technologique et numérique',
    subjects: ['Formation manuelle, technique, technologique et numérique'],
    domains: [],
  },
  {
    key: 'artistique',
    label: 'Éducation culturelle et artistique',
    subjects: ['Éducation culturelle et artistique'],
    domains: [],
  },
  {
    key: 'eps',
    label: 'Éducation physique, bien-être et santé',
    subjects: ['Éducation physique, bien-être et santé'],
    domains: [],
  },
  {
    key: 'philo-citoy',
    label: 'Éducation à la philosophie et à la citoyenneté',
    subjects: ['Éducation à la philosophie et à la citoyenneté'],
    domains: [],
  },
  {
    key: 'cours-philo',
    label: 'Cours philosophiques',
    subjects: ['Cours philosophiques'],
    domains: [],
  },
  {
    key: 'langues',
    label: 'Langues modernes',
    subjects: ['Langues modernes'],
    domains: [],
  },
];

const BY_KEY = new Map(BULLETIN_SUBJECTS.map((b) => [b.key, b]));

const BY_SUBJECT = new Map<string, BulletinSubject>();
for (const block of BULLETIN_SUBJECTS) {
  for (const subject of block.subjects) BY_SUBJECT.set(subject, block);
}

export function bulletinSubjectByKey(key: string): BulletinSubject | undefined {
  return BY_KEY.get(key);
}

/**
 * The block an evaluation belongs to.
 *
 * Undefined for a subject that is no longer part of `SUBJECTS` — an old record
 * from a renamed subject the migration missed. The caller shows it apart rather
 * than dropping it.
 */
export function bulletinSubjectFor(subject: string): BulletinSubject | undefined {
  return BY_SUBJECT.get(subject);
}

/** True when a subject's evaluations land in this block. */
export function blockCovers(block: BulletinSubject, subject: string): boolean {
  return block.subjects.includes(subject);
}

/**
 * The report domain suggested for a lesson domain.
 *
 * Only a suggestion: the dialog pre-selects it and the teacher can change it.
 * A lesson domain that is not listed, or a subject without domains, suggests
 * nothing.
 */
const BULLETIN_DOMAIN_BY_LESSON_DOMAIN: Readonly<Record<string, string>> = {
  // Français
  Lire: 'Lire',
  Écrire: 'Écrire',
  Grammaire: 'Écrire',
  Conjugaison: 'Écrire',
  Orthographe: 'Écrire',
  Vocabulaire: 'Écrire',
  Parler: 'Parler',
  Écouter: 'Écouter',
  // Mathématiques
  'Arithmétique et algèbre': 'Nombres',
  'Des objets de l’espace à la géométrie': 'Solides et figures',
  Grandeurs: 'Grandeurs',
  'Organisation des données et statistiques': 'Traitement de données',
};

export function suggestedBulletinDomain(subject: string, lessonDomain: string): string {
  const block = bulletinSubjectFor(subject);
  if (!block || block.domains.length === 0) return '';

  const suggested = BULLETIN_DOMAIN_BY_LESSON_DOMAIN[lessonDomain] ?? '';
  return block.domains.includes(suggested) ? suggested : '';
}

/**
 * Lesson domains that feed a given report domain, for filtering the lesson
 * list. An empty report domain means "no filter".
 */
export function lessonDomainsFor(bulletinDomain: string): readonly string[] {
  if (bulletinDomain === '') return [];
  return Object.entries(BULLETIN_DOMAIN_BY_LESSON_DOMAIN)
    .filter(([, value]) => value === bulletinDomain)
    .map(([key]) => key);
}

/** Curriculum subjects that no bulletin block claims. Expected to be empty. */
export function unassignedSubjects(): readonly string[] {
  return CURRICULUM_SUBJECT_NAMES.filter((name) => !BY_SUBJECT.has(name));
}
